import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export type MobileAccessStatusValue =
  | 'None'
  | 'PendingInvitation'
  | 'Active'
  | 'Suspended'
  | 'Revoked';

export type CustomerAccessRole = 'AccountAdmin' | 'Operations' | 'Finance' | 'ReadOnly';

export interface MobileAccessUser {
  linkId: string;
  userId: string;
  username: string;
  fullName: string;
  phoneNumber: string | null;
  email: string;
  status: MobileAccessStatusValue;
  invitedAt: string | null;
  activatedAt: string | null;
  revokedAt: string | null;
}

export interface MobileAccessStatus {
  customerId: string;
  customerName: string;
  status: MobileAccessStatusValue;
  users: MobileAccessUser[];
}

export interface CustomerMember {
  membershipId: string;
  userId: string;
  fullName: string;
  username: string;
  phoneNumber: string | null;
  email: string;
  jobTitle: string | null;
  accessRole: CustomerAccessRole;
  status: MobileAccessStatusValue;
  invitedAt: string | null;
  activatedAt: string | null;
  suspendedAt: string | null;
  revokedAt: string | null;
  lastAccessedAt: string | null;
}

export interface CustomerMemberPage {
  items: CustomerMember[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

export interface InviteCustomerMember {
  firstName: string;
  otherNames?: string;
  phoneNumber: string;
  email?: string;
  accessRole: CustomerAccessRole;
  jobTitle?: string;
}

const base = (customerId: string) => `/Customers/${customerId}/mobile-access`;
const membersBase = (customerId: string) => `/customers/${customerId}/mobile-members`;

export const mobileAccessApi = {
  getStatus: async (customerId: string): Promise<MobileAccessStatus> => {
    const { data } = await apiClient.get<ApiResponse<MobileAccessStatus>>(base(customerId));
    return data.data;
  },

  enable: async (customerId: string): Promise<MobileAccessStatus> => {
    const { data } = await apiClient.post<ApiResponse<MobileAccessStatus>>(`${base(customerId)}/enable`, {});
    return data.data;
  },

  resend: async (customerId: string): Promise<MobileAccessStatus> => {
    const { data } = await apiClient.post<ApiResponse<MobileAccessStatus>>(`${base(customerId)}/resend`, {});
    return data.data;
  },

  revoke: async (customerId: string, reason?: string): Promise<MobileAccessStatus> => {
    const { data } = await apiClient.post<ApiResponse<MobileAccessStatus>>(`${base(customerId)}/revoke`, { reason });
    return data.data;
  },

  getMembers: async (customerId: string, pageNumber = 1, pageSize = 25): Promise<CustomerMemberPage> => {
    const { data } = await apiClient.get<ApiResponse<CustomerMemberPage>>(membersBase(customerId), {
      params: { pageNumber, pageSize },
    });
    return data.data;
  },

  inviteMember: async (customerId: string, payload: InviteCustomerMember): Promise<CustomerMember> => {
    const { data } = await apiClient.post<ApiResponse<CustomerMember>>(`${membersBase(customerId)}/invitations`, payload);
    return data.data;
  },

  changeMemberRole: async (customerId: string, membershipId: string, accessRole: CustomerAccessRole): Promise<CustomerMember> => {
    const { data } = await apiClient.patch<ApiResponse<CustomerMember>>(`${membersBase(customerId)}/${membershipId}/role`, { accessRole });
    return data.data;
  },

  memberAction: async (
    customerId: string,
    membershipId: string,
    action: 'resend-invitation' | 'suspend' | 'reactivate' | 'revoke' | 'reinvite',
    reason?: string,
  ): Promise<CustomerMember> => {
    const { data } = await apiClient.post<ApiResponse<CustomerMember>>(
      `${membersBase(customerId)}/${membershipId}/${action}`,
      reason ? { reason } : {},
    );
    return data.data;
  },
};
